import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import { COUNTRY } from 'common/constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { UpdatePatientDetails } from 'connect/saga/handlers/patient/update-details';
import { selectDataClinicCountry } from 'connect/selectors';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormCheckbox, FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form, StepFormWrapper, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { StepHeader, StepHeaderSubtext } from 'pages/checkout/components/common/layout-styled-components';
import { SmsPromoDisclaimer } from 'pages/checkout/components/common/SmsPromoDisclaimer';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { AllowSmsCheckboxWrapper, Bolded, DisclaimerContainer, PhoneNumberSubmitButton, SmsHelpInfo } from 'pages/checkout/steps/PhoneNumber/styled-components';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';

const analytics = new Analytics();

const PHONE_NUMBER_FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'phone', preset: ValidationRulePreset.phone },
]);

interface ValidationState {
  allValid?: boolean,
  phone?: FieldValidation,
}

export const PhoneNumber = () => {
  const dispatch = useDispatch();
  const { goToNextStep } = useNextCheckoutStep();
  const analyticsData = useCheckoutRedesignAnalyticsData();
  const formValidator = useFormValidator(PHONE_NUMBER_FORM_VALIDATOR_CONFIG);

  const clinicCountry = useSelector(selectDataClinicCountry);

  const isLoading = useSelector(UpdatePatientDetails.loading!.selector);
  const error = useSelector(UpdatePatientDetails.error!.selector);
  const success = useSelector(UpdatePatientDetails.success!.selector);

  const [shouldAutoValidate, setShouldAutoValidate] = useState(false);
  const [phone, setPhone] = useState<string>('');
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(false);
  const [formValidity, setFormValidity] = useState<ValidationState>({});

  const validationState = useMemo(() => ({ phone }), [phone]);

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-phone-number',
      name: 'checkout',
      section: 'phone-number',
      ...analyticsData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  const handleValidate = ({ shouldValidate = true, stateOverride }: {
    shouldValidate?: boolean;
    stateOverride?: Record<string, any>;
  } = {}) => {
    if (!shouldValidate) {
      // Not needed, but avoids TS error
      return {
        allValid: null,
      };
    }

    const validationStateToUse = {
      ...validationState,
      ...stateOverride,
    };

    const validationResult = formValidator.validate(validationStateToUse);
    setFormValidity(validationResult);
    return validationResult;
  };

  useEffect(() => {
    if (success && !isLoading) {
      dispatch(UpdatePatientDetails.success!.update(null));

      analytics.trackForm({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: 'checkout-phone-number',
        name: 'checkout',
        section: 'phone-number',
        ...analyticsData,
        content: {
          phone_provided: String(!!phone),
          promo_sms_allowed: String(promoSmsAllowed),
        },
      });

      // Use replace because this step will become inapplicable
      // As soon as the phone number is provided.
      // If we don't use replace, the user can't go back using the browser's back button.
      goToNextStep({ replace: true });
    }
  }, [success, isLoading, dispatch, goToNextStep, phone, promoSmsAllowed, analyticsData]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    handleValidate({
      shouldValidate: shouldAutoValidate,
      stateOverride: { phone: newPhone },
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      // Validate on change only after first submission
      setShouldAutoValidate(true);
      return;
    }

    dispatch(UpdatePatientDetails.createAction({
      mobilePhone: `+1${phone}`,
      settings: {
        promoSmsAllowed,
      },
    }));
  };

  const handleSmsPromoToggle = () => {
    const newPromoSmsAllowed = !promoSmsAllowed;
    setPromoSmsAllowed(newPromoSmsAllowed);

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-phone-number',
      name: 'checkout',
      section: 'phone-number',
      content: {
        action: 'sms-promo-toggle',
        sms_promo_allowed: String(newPromoSmsAllowed),
        ...analyticsData.content,
      },
    });
  };

  return (
    <StepWrapper>
      <StepHeader>
        <StepHeaderText>Enter Phone Number</StepHeaderText>
        <StepHeaderSubtext>Let us know how to contact you.</StepHeaderSubtext>
      </StepHeader>

      <StepFormWrapper>
        {error && <FormError>{error}</FormError>}
        <Form onSubmit={handleSubmit}>
          <Input
            type={InputType.TEL}
            placeholder="Enter your phone"
            autoComplete="tel-national"
            name="phone"
            value={phone}
            onChange={handlePhoneChange}
            errorMessage={formValidity.phone?.message}
          />
          {clinicCountry === COUNTRY.unitedStates
            && (
              <AllowSmsCheckboxWrapper>
                <FormCheckbox
                  checked={promoSmsAllowed}
                  onChange={handleSmsPromoToggle}
                />
                I agree to receive SMS with promotional content at the phone number provided above.
              </AllowSmsCheckboxWrapper>
            )}

          <PhoneNumberSubmitButton type="submit" $loading={isLoading}>
            Continue Booking
          </PhoneNumberSubmitButton>
        </Form>
      </StepFormWrapper>

      <DisclaimerContainer>
        <SmsPromoDisclaimer />

        <SmsHelpInfo>
          Text
          <Bolded>STOP</Bolded>
          to opt out or
          <Bolded>HELP</Bolded>
          for help.
        </SmsHelpInfo>
      </DisclaimerContainer>
    </StepWrapper>
  );
};
